import React, { useMemo, useState } from 'react';
import { Plus, Upload, Send, Search, Link2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ConfirmDeleteDialog } from '@/components/ConfirmDeleteDialog';
import { oapi, copyText } from '@/lib/officialsApi';
import { OfficialRow } from './OfficialRow';
import { OfficialFormDialog } from './OfficialFormDialog';
import { ImportOfficialsDialog } from './ImportOfficialsDialog';
import { OfficialDetailDialog } from './OfficialDetailDialog';

const EmptyState = ({ onAdd, onImport, joinLink }) => (
  <div className="bg-white border rounded-2xl px-6 py-14 text-center" data-testid="officials-empty">
    <span className="w-14 h-14 mx-auto rounded-2xl bg-[#00247D]/10 text-[#00247D] flex items-center justify-center"><UserPlus className="w-6 h-6" /></span>
    <h3 className="mt-4 text-2xl font-black uppercase text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>Build your officials list</h3>
    <p className="mt-2 text-sm text-slate-600 max-w-md mx-auto">Add officials one by one, import a spreadsheet, or share your league link so they can sign up themselves.</p>
    <div className="mt-6 flex flex-wrap justify-center gap-2">
      <Button onClick={onAdd} className="bg-[#00247D] hover:bg-[#001A5C]" data-testid="empty-add-official-btn"><Plus className="w-4 h-4 mr-1.5" />Add official</Button>
      <Button variant="outline" onClick={onImport} data-testid="empty-import-btn"><Upload className="w-4 h-4 mr-1.5" />Import CSV / Excel</Button>
      {joinLink && <Button variant="outline" onClick={() => copyText(joinLink, 'Sign-up link')} data-testid="empty-copy-join-link"><Link2 className="w-4 h-4 mr-1.5" />Copy sign-up link</Button>}
    </div>
  </div>
);

export const OfficialsDatabaseTab = ({ orgId, officials, roles, joinLink, reload }) => {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('all');
  const [sport, setSport] = useState('all');
  const [selected, setSelected] = useState(new Set());
  const [form, setForm] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [del, setDel] = useState(null);

  const sports = useMemo(() => [...new Set(officials.flatMap(o => (o.sports || []).map(s => s.toLowerCase())))].sort(), [officials]);
  const list = officials.filter(o => (status === 'all' || o.status === status)
    && (sport === 'all' || (o.sports || []).some(s => s.toLowerCase() === sport))
    && (!q || `${o.full_name} ${o.email || ''} ${o.phone || ''}`.toLowerCase().includes(q.toLowerCase())));
  const roleLabel = (k) => roles.find(r => r.key === k)?.label || k;
  const toggle = (id) => setSelected(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const invite = async (ids) => {
    try {
      const r = await oapi('/officials/invite', { method: 'POST', orgId, body: { official_ids: ids } });
      toast.success(`${r.sent} invite${r.sent === 1 ? '' : 's'} sent${r.skipped.length ? ` · ${r.skipped.length} skipped (already joined or no email)` : ''}`);
      setSelected(new Set());
      reload();
    } catch (e) { toast.error(e.message); }
  };
  const remove = async () => {
    try {
      const r = await oapi(`/officials/list/${del.id}`, { method: 'DELETE', orgId });
      toast.success(`${del.full_name} removed${r.cancelled_assignments ? ` · ${r.cancelled_assignments} assignment(s) cancelled` : ''}`);
      setDel(null);
      reload();
    } catch (e) { toast.error(e.message); }
  };
  const actions = { onOpen: (o) => setDetailId(o.id), onEdit: setForm, onInvite: (o) => invite([o.id]), onDelete: setDel };

  return (
    <div className="space-y-4" data-testid="officials-database-tab">
      {officials.length === 0 ? <EmptyState onAdd={() => setForm({})} onImport={() => setImportOpen(true)} joinLink={joinLink} /> : (
        <>
          <div className="flex flex-wrap items-center gap-2 bg-white border rounded-xl p-2.5">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
              <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, email or phone" className="pl-8 h-9" data-testid="officials-search-input" />
            </div>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-32 h-9" data-testid="officials-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All statuses</SelectItem><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
            </Select>
            {sports.length > 0 && (
              <Select value={sport} onValueChange={setSport}>
                <SelectTrigger className="w-36 h-9 capitalize" data-testid="officials-sport-filter"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All sports</SelectItem>{sports.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
              </Select>
            )}
            <div className="flex gap-2 ml-auto">
              {selected.size > 0 && <Button variant="outline" size="sm" className="h-9" onClick={() => invite([...selected])} data-testid="officials-invite-selected-btn"><Send className="w-4 h-4 mr-1.5" />Invite {selected.size}</Button>}
              <Button variant="outline" size="sm" className="h-9" onClick={() => setImportOpen(true)} data-testid="officials-import-btn"><Upload className="w-4 h-4 mr-1.5" />Import</Button>
              <Button size="sm" className="h-9 bg-[#00247D] hover:bg-[#001A5C]" onClick={() => setForm({})} data-testid="officials-add-btn"><Plus className="w-4 h-4 mr-1.5" />Add official</Button>
            </div>
          </div>
          {joinLink && (
            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600 bg-[#00247D]/5 border border-[#00247D]/10 rounded-xl px-3 py-2" data-testid="officials-join-link-strip">
              <Link2 className="w-4 h-4 text-[#00247D]" />Officials can also sign up themselves with your league link.
              <button type="button" onClick={() => copyText(joinLink, 'Sign-up link')} className="font-bold text-[#00247D] hover:underline" data-testid="officials-copy-join-link">Copy link</button>
            </div>
          )}
          <div className="bg-white border rounded-xl overflow-hidden" data-testid="officials-list">
            <div className="flex items-center gap-3 px-3 sm:px-4 py-2 border-b bg-slate-50 text-[11px] font-bold uppercase tracking-wide text-slate-500">
              <input type="checkbox" className="accent-[#00247D]" checked={list.length > 0 && list.every(o => selected.has(o.id))} data-testid="officials-select-all"
                onChange={e => setSelected(e.target.checked ? new Set(list.map(o => o.id)) : new Set())} />
              <span>{list.length} official{list.length === 1 ? '' : 's'}</span>
            </div>
            {list.map(o => <OfficialRow key={o.id} o={o} selected={selected.has(o.id)} onSelect={toggle} roleLabel={roleLabel} {...actions} />)}
            {list.length === 0 && <p className="py-10 text-center text-sm text-slate-500">No officials match these filters.</p>}
          </div>
        </>
      )}
      <OfficialFormDialog open={!!form} orgId={orgId} official={form?.id ? form : null} roles={roles}
        onClose={() => setForm(null)} onSaved={() => { setForm(null); reload(); }} />
      <ImportOfficialsDialog open={importOpen} orgId={orgId} onClose={() => setImportOpen(false)} onDone={reload} />
      <OfficialDetailDialog open={!!detailId} orgId={orgId} officialId={detailId} roleLabel={roleLabel} onClose={() => setDetailId(null)}
        onEdit={(o) => { setDetailId(null); setForm(o); }} onInvite={(o) => invite([o.id])} />
      <ConfirmDeleteDialog open={!!del} title="Remove this official?" itemName={del?.full_name} confirmLabel="Remove official"
        description="will be removed from your officials list. Their upcoming assignments are cancelled and they are emailed."
        onClose={() => setDel(null)} onConfirm={remove} testId="delete-official" />
    </div>
  );
};

export default OfficialsDatabaseTab;
